/**
 * SqlPreviewBlock — collapsible read-only SQL display with "Copy" affordance,
 * plus opt-in editable mode (PR-A5).
 *
 * Pure presentational primitive (PR-A2 of the V2 dashboard parity plan).
 * Consumed by THREE separate surfaces so the chrome must NOT depend on app-
 * level state:
 *
 *   1. ``<StructuredDataCard>``    — inline chat result (PR-A2)
 *   2. ``<EnterpriseDataPanel>``   — top-of-route panel in /workspace/dashboard
 *                                    (PR-A3 read-only; PR-A5 toggles editable=true)
 *   3. ``<ChartSqlEditor>``        — Stream A PR-4 inline editor (uses Monaco directly,
 *                                    not this primitive)
 *
 * Behavior contract (read-only mode — default):
 *   - First 3 lines visible when collapsed; "Mostra tutto" expands.
 *   - "Copia SQL" copies via ``navigator.clipboard`` + toast (sonner) hook
 *     resolved from ``./hooks/use-toast`` — falling back silently when the
 *     Clipboard API is unavailable (older browsers / restricted iframes).
 *   - Monospace + light cream surface + ink-2 text (no syntax-aware
 *     colorisation — Monaco lives in ``@ando/ui/editors`` and is intentionally
 *     NOT pulled into this lightweight primitive).
 *
 * Behavior contract (editable mode — ``editable=true``):
 *   - Renders a monospace ``<textarea>`` with the SQL as controlled value
 *     (``value`` + ``onChange``). Lightweight by design — Monaco stays in
 *     ``@ando/ui/editors`` so dashboards / chat keep their lean bundle.
 *   - Optional "Ri-esegui" action button + spinner via ``onReExecute`` /
 *     ``isExecuting``.
 *   - Cmd/Ctrl+Enter triggers ``onReExecute`` for power-user ergonomics.
 *   - The expander / collapse chrome is hidden in editable mode (the full
 *     editor is always visible). The "Copia SQL" affordance remains.
 *
 * Backward compatibility:
 *   - ``editable`` defaults to ``false``. Existing PR-A2 consumers that
 *     pass only ``sql`` keep their behaviour bit-for-bit.
 *
 * Storybook-friendly: zero imports from ``@/features/*`` or routing —
 * just primitives + tailwind + a sonner-backed toast hook from the same
 * package.
 */

import * as React from "react";
import { Check, ChevronDown, ChevronUp, Copy, Loader2, Play } from "lucide-react";
import { msg } from "@lingui/core/macro";
import { useLingui } from "@lingui/react/macro";
import { Button } from "../primitives/button";
import { cn } from "../lib/cn";
import { useToast } from "../hooks/use-toast";

const COLLAPSED_LINE_COUNT = 3;

/** Read-only props — preserved verbatim from PR-A2. */
interface SqlPreviewBlockReadOnlyProps {
  /** SQL string to render. Falsy → component renders nothing. */
  readonly sql: string;
  /** Initial expanded state. Defaults to ``false`` (3-line preview). */
  readonly defaultExpanded?: boolean;
  /** Fires after a successful clipboard write — consumer can log/track. */
  readonly onCopy?: () => void;
  /** Optional extra classes appended to the root container. */
  readonly className?: string;
  /** Explicit read-only marker (defaults to undefined ⇒ read-only). */
  readonly editable?: false;
  /** Not used in read-only mode — typed so the union narrows cleanly. */
  readonly value?: never;
  readonly onChange?: never;
  readonly onReExecute?: never;
  readonly isExecuting?: never;
  readonly editorAriaLabel?: never;
}

/** Editable props — opt-in via ``editable=true`` (PR-A5). */
interface SqlPreviewBlockEditableProps {
  /** Original / fallback SQL — used by "Copia SQL" + as the textarea fallback. */
  readonly sql: string;
  /** Mode toggle — when ``true`` the primitive becomes a controlled editor. */
  readonly editable: true;
  /** Controlled editor buffer. Falls back to ``sql`` when omitted. */
  readonly value?: string;
  /** Fires on every keystroke (no debounce — parents own debouncing). */
  readonly onChange?: (next: string) => void;
  /**
   * Optional re-execute callback. When provided, a "Ri-esegui" button shows
   * alongside "Copia SQL". Receives the current buffer (``value ?? sql``).
   */
  readonly onReExecute?: (currentSql: string) => void;
  /** When ``true``, the editor goes read-only + the button shows a spinner. */
  readonly isExecuting?: boolean;
  /** Override the textarea's ``aria-label``. Defaults to ``"Editor SQL"``. */
  readonly editorAriaLabel?: string;
  /** Fires after a successful clipboard write — consumer can log/track. */
  readonly onCopy?: () => void;
  /** Optional extra classes appended to the root container. */
  readonly className?: string;
  /** Editable mode is always "fully shown" — defaultExpanded is ignored. */
  readonly defaultExpanded?: never;
}

export type SqlPreviewBlockProps =
  | SqlPreviewBlockReadOnlyProps
  | SqlPreviewBlockEditableProps;

/**
 * Split the SQL into visible / hidden parts based on newline count. We
 * preserve trailing newlines so the preview reads naturally when collapsed
 * (i.e. the "first 3 lines" includes the line-break that ends line 3).
 */
function splitForPreview(sql: string): { head: string; rest: string } {
  const lines = sql.split("\n");
  if (lines.length <= COLLAPSED_LINE_COUNT) {
    return { head: sql, rest: "" };
  }
  const head = lines.slice(0, COLLAPSED_LINE_COUNT).join("\n");
  const rest = lines.slice(COLLAPSED_LINE_COUNT).join("\n");
  return { head, rest };
}

export const SqlPreviewBlock: React.FC<SqlPreviewBlockProps> = (props) => {
  const { sql, onCopy, className } = props;
  const editable = props.editable === true;
  const { i18n } = useLingui();
  const { toast } = useToast();

  // Read-only-only state — kept at the top so Hook order is stable across
  // the editable / non-editable branches.
  const [expanded, setExpanded] = React.useState(
    !editable && props.defaultExpanded === true,
  );
  const [copied, setCopied] = React.useState(false);
  const copyTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset the "copied" tick when the SQL itself changes (e.g. user re-runs
  // the same card with a new query) so the green check doesn't linger.
  React.useEffect(() => {
    setCopied(false);
    if (copyTimerRef.current !== null) {
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
    };
  }, []);

  // Effective SQL for "Copia SQL" — in editable mode we copy what the
  // user is currently looking at (the live buffer), not the prop snapshot.
  const liveSql = editable ? (props.value ?? sql) : sql;

  const handleCopy = React.useCallback(async () => {
    if (!liveSql) return;
    try {
      await navigator.clipboard.writeText(liveSql);
      setCopied(true);
      if (copyTimerRef.current !== null) clearTimeout(copyTimerRef.current);
      copyTimerRef.current = setTimeout(() => setCopied(false), 1800);
      toast.success(i18n._(msg`SQL copiato`));
      onCopy?.();
    } catch {
      // Clipboard API unavailable / blocked — surface a friendly toast
      // instead of throwing. Consumers can still ``onCopy`` themselves.
      toast.error(i18n._(msg`Impossibile copiare`), {
        description: i18n._(msg`Il browser ha bloccato l'accesso agli appunti.`),
      });
    }
  }, [liveSql, toast, onCopy, i18n]);

  const handleReExecute = React.useCallback(() => {
    if (!editable) return;
    const cb = props.onReExecute;
    if (typeof cb !== "function") return;
    cb(liveSql);
  }, [editable, liveSql, props]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (!editable) return;
      if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
        event.preventDefault();
        if (!props.isExecuting) handleReExecute();
      }
    },
    [editable, handleReExecute, props],
  );

  if (!sql && !editable) return null;
  // In editable mode we still render even when the buffer is empty — the
  // user might be starting from scratch (theoretical, the panel always
  // seeds with display.sql).

  if (editable) {
    return (
      <section
        className={cn(
          "rounded-md border border-hairline bg-cream-deep/40",
          className,
        )}
      >
        <header className="flex items-center justify-between gap-2 border-hairline border-b px-3 py-1.5">
          <span className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
            SQL
          </span>
          <div className="flex items-center gap-1">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label={copied ? i18n._(msg`SQL copiato`) : i18n._(msg`Copia SQL`)}
              onClick={handleCopy}
              disabled={!liveSql}
            >
              {copied ? (
                <>
                  <Check aria-hidden />
                  {i18n._(msg`Copiato`)}
                </>
              ) : (
                <>
                  <Copy aria-hidden />
                  {i18n._(msg`Copia SQL`)}
                </>
              )}
            </Button>
            {props.onReExecute ? (
              <Button
                type="button"
                variant="primary"
                size="sm"
                onClick={handleReExecute}
                disabled={
                  props.isExecuting === true || liveSql.trim().length === 0
                }
              >
                {props.isExecuting ? (
                  <Loader2 size={12} className="animate-spin" aria-hidden />
                ) : (
                  <Play size={12} aria-hidden />
                )}
                {i18n._(msg`Ri-esegui`)}
              </Button>
            ) : null}
          </div>
        </header>
        <textarea
          aria-label={props.editorAriaLabel ?? i18n._(msg`Editor SQL`)}
          value={liveSql}
          readOnly={props.isExecuting === true}
          onChange={(event) => props.onChange?.(event.target.value)}
          onKeyDown={handleKeyDown}
          spellCheck={false}
          rows={Math.min(Math.max(liveSql.split("\n").length, 4), 12)}
          className={cn(
            "block w-full resize-y bg-transparent px-3 py-2",
            "font-mono text-[11px] text-ink-1 leading-relaxed",
            "outline-none focus:bg-cream-deep/60",
            "disabled:cursor-not-allowed disabled:opacity-60",
          )}
        />
        <footer className="border-hairline border-t px-3 py-1 text-[10px] text-ink-3">
          {i18n._(msg`Cmd/Ctrl + Invio per eseguire`)}
        </footer>
      </section>
    );
  }

  // ── Read-only mode (PR-A2 behaviour, unchanged) ────────────────────────
  const { head, rest } = splitForPreview(sql);
  const hasMore = rest.length > 0;
  const showAll = expanded || !hasMore;

  return (
    <section
      className={cn(
        "rounded-md border border-hairline bg-cream-deep/40",
        className,
      )}
    >
      <header className="flex items-center justify-between gap-2 border-hairline border-b px-3 py-1.5">
        <span className="font-medium text-[10px] text-ink-3 uppercase tracking-[0.06em]">
          SQL
        </span>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          aria-label={copied ? i18n._(msg`SQL copiato`) : i18n._(msg`Copia SQL`)}
          onClick={handleCopy}
        >
          {copied ? (
            <>
              <Check aria-hidden />
              {i18n._(msg`Copiato`)}
            </>
          ) : (
            <>
              <Copy aria-hidden />
              {i18n._(msg`Copia SQL`)}
            </>
          )}
        </Button>
      </header>
      <pre
        className={cn(
          "overflow-x-auto px-3 py-2 font-mono text-[11px] text-ink-2 leading-relaxed",
          "whitespace-pre-wrap break-words",
        )}
      >
        {showAll ? sql : head}
      </pre>
      {hasMore ? (
        <button
          type="button"
          onClick={() => setExpanded((prev) => !prev)}
          aria-expanded={expanded}
          className={cn(
            "flex w-full items-center justify-center gap-1 border-hairline border-t px-3 py-1.5",
            "text-[10px] text-ink-3 uppercase tracking-[0.06em]",
            "transition-colors hover:bg-cream-deep hover:text-ink-1",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/40",
          )}
        >
          {expanded ? (
            <>
              <ChevronUp size={12} aria-hidden />
              {i18n._(msg`Mostra meno`)}
            </>
          ) : (
            <>
              <ChevronDown size={12} aria-hidden />
              {i18n._(msg`Mostra tutto`)}
            </>
          )}
        </button>
      ) : null}
    </section>
  );
};
SqlPreviewBlock.displayName = "SqlPreviewBlock";
